import { Controller } from "@hotwired/stimulus"
import { skeletonLoader } from '../utils/skeleton_loader'

export default class extends Controller {
  static targets = ["dropdown", "button", "indicator"]

  connect() {
    this.dropdownTarget.classList.add('hidden')
    this.buttonTarget.setAttribute('aria-expanded', 'false')
    this.isOpen = false
    this.setupClickOutside()
  }

  disconnect() {
    this.teardownClickOutside()
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.dropdownTarget.classList.remove('hidden')
    this.dropdownTarget.classList.add('block')
    this.buttonTarget.setAttribute('aria-expanded', 'true')
    this.isOpen = true
  }

  close() {
    this.dropdownTarget.classList.remove('block')
    this.dropdownTarget.classList.add('hidden')
    this.buttonTarget.setAttribute('aria-expanded', 'false')
    this.isOpen = false
  }

  setSortAndSubmit(event) {
    event.preventDefault()
    const button = event.currentTarget
    const sortColumn = button.dataset.sortColumn
    const defaultDirection = (button.dataset.defaultDirection || "desc").toLowerCase()
    const currentSortColumnField = document.getElementById("sort_column_hidden")
    const currentSortDirectionField = document.getElementById("sort_direction_hidden")
    const currentColumn = currentSortColumnField && currentSortColumnField.value
    const currentDirection = (currentSortDirectionField && currentSortDirectionField.value || defaultDirection).toLowerCase()
    const isSameColumn = currentColumn === sortColumn
    const newDirection = isSameColumn
      ? (currentDirection === "asc" ? "desc" : "asc")
      : defaultDirection
    // Update dataset on the button so highlighting logic sees the new state
    button.dataset.sortDirection = newDirection
    // Update label based on provided asc/desc labels
    const labelAsc = button.dataset.labelAsc
    const labelDesc = button.dataset.labelDesc
    const labelElement = button.querySelector('[data-sort-label]')
    if (labelElement && (labelAsc || labelDesc)) {
      labelElement.textContent = newDirection === "asc" ? labelAsc : labelDesc
    }
    // Update arrow text (↑ for asc, ↓ for desc)
    const arrowElement = button.querySelector('[data-sort-arrow]')
    if (arrowElement) {
      arrowElement.textContent = newDirection === "desc" ? "↓" : "↑"
    }
    // Update hidden fields
    if (currentSortColumnField) currentSortColumnField.value = sortColumn
    if (currentSortDirectionField) currentSortDirectionField.value = newDirection
    // Update dropdown option highlighting immediately (including checkmark)
    this.updateDropdownHighlighting(sortColumn, newDirection)
    // Update indicator ring on the main sort button
    this.updateIndicator()
    // Keep dropdown open (do NOT call this.close())

    // Show skeleton loaders before form submission
    skeletonLoader.show({ position: 'prepend' })

    // Submit form
    const searchForm = document.getElementById("search-form-tab")
    if (searchForm) {
      setTimeout(() => {
        try {
          searchForm.requestSubmit()
        } catch (error) {
          console.error("requestSubmit failed:", error)
          try {
            searchForm.submit()
          } catch (error2) {
            console.error("submit also failed:", error2)
          }
        }
      }, 100)
    } else {
      console.error("Search form not found!")
    }
  }

  updateDropdownHighlighting(activeColumn, activeDirection) {
    // Find all sort option buttons in the dropdown
    const sortButtons = this.dropdownTarget.querySelectorAll('button[data-sort-column]')
    sortButtons.forEach(button => {
      const buttonColumn = button.dataset.sortColumn
      const buttonDirection = button.dataset.sortDirection
      const isActive = buttonColumn === activeColumn && buttonDirection === activeDirection
      const arrow = button.querySelector('[data-sort-arrow]')
      if (isActive) {
        // Apply active styling
        button.classList.remove('text-gray-700', 'hover:text-gray-900')
        button.classList.add('text-gray-900', 'font-semibold')
        if (arrow) {
          arrow.classList.remove('hidden')
        }
      } else {
        // Apply inactive styling
        button.classList.remove('text-gray-900', 'font-semibold')
        button.classList.add('text-gray-700', 'hover:text-gray-900')
        if (arrow) {
          arrow.classList.add('hidden')
        }
      }
    })
  }

  updateIndicator() {
    const sortColumnField = document.getElementById('sort_column_hidden')

    if (sortColumnField && sortColumnField.value) {
      // Add ring border when sorting is active
      this.buttonTarget.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2')
    } else {
      // Remove ring border when no sorting is applied
      this.buttonTarget.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2')
    }
  }

  setupClickOutside() {
    this.clickOutsideHandler = (event) => {
      if (!this.element.contains(event.target)) {
        this.close()
      }
    }
    document.addEventListener('click', this.clickOutsideHandler)
  }

  teardownClickOutside() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler)
    }
  }

  // Handle keyboard navigation
  keydown(event) {
    if (event.key === 'Escape') {
      this.close()
      this.buttonTarget.focus()
    }
  }
}
